import mitt, { Emitter, Handler } from './SandboxMitt';
import { PropRuntimeEvent, PropRuntimeEventType } from './PropMechanicsTypes';

/** 运行时事件监听函数。 */
export type PropEventListener = (event: PropRuntimeEvent) => void;
export type PropEventListenerPhase = 'normal' | 'after';

type PropEventMap = Record<PropRuntimeEventType, PropRuntimeEvent>;

interface PropEventSubscription {
    type: PropRuntimeEventType;
    listener: PropEventListener;
    handler?: Handler<PropRuntimeEvent>;
    scope?: string;
    phase: PropEventListenerPhase;
}

/** 事件完成分发后的观察回调。 */
type PropEventAfterEmitListener = (event: PropRuntimeEvent) => void;

/**
 * 道具运行时事件总线。
 *
 * 底层使用 mitt 完成同步事件分发，外层补充运行时需要的管理能力：
 * - scope：按回合、实例或模块批量清理监听，避免临时监听泄漏。
 * - once：只执行一次的监听，适合等待某个流程事件。
 * - 强类型事件：所有事件统一使用 PropRuntimeEvent，便于日志记录、调试和回放。
 *
 * BattleManager 负责派发流程事件；
 * PropsMechanicsEngine 订阅事件并驱动道具运行时图；
 * 表现层也可以订阅同一总线，用于播放动画、刷新界面或记录日志。
 */
export class PropEventBus {
    private readonly emitter: Emitter<PropEventMap> = mitt<PropEventMap>();
    private readonly subscriptions: PropEventSubscription[] = [];
    private readonly afterEmitListeners: PropEventAfterEmitListener[] = [];

    /** 注册常驻监听，并返回注销函数。 */
    public on(
        type: PropRuntimeEventType,
        listener: PropEventListener,
        scope?: string,
        phase: PropEventListenerPhase = 'normal',
    ): () => void {
        if (phase === 'after') {
            this.subscriptions.push({ type, listener, scope, phase });
            return () => this.off(listener);
        }
        const handler: Handler<PropRuntimeEvent> = (event) => listener(event);
        this.emitter.on(type, handler);
        this.subscriptions.push({ type, listener, handler, scope, phase });
        return () => this.off(listener);
    }

    /** 注册只执行一次的监听，并返回注销函数。 */
    public once(type: PropRuntimeEventType, listener: PropEventListener, scope?: string): () => void {
        const handler: Handler<PropRuntimeEvent> = (event) => {
            this.off(listener);
            listener(event);
        };
        this.emitter.on(type, handler);
        this.subscriptions.push({ type, listener, handler, scope, phase: 'normal' });
        return () => this.off(listener);
    }

    /**
     * 注册事件分发完成后的观察回调。
     *
     * 该回调会在 mitt 同步执行完当前事件的所有普通监听器之后触发。
     * 它适合做日志、调试快照、UI 刷新信号等“观察事件结果”的工作。
     *
     * @param listener 事件分发完成后的回调函数。
     * @returns 注销该回调的函数。
     */
    public onAfterEmit(listener: PropEventAfterEmitListener): () => void {
        this.afterEmitListeners.push(listener);
        return () => {
            const index = this.afterEmitListeners.indexOf(listener);
            if (index >= 0) this.afterEmitListeners.splice(index, 1);
        };
    }

    /** 注销指定监听函数。 */
    public off(listener: PropEventListener): void {
        for (let i = this.subscriptions.length - 1; i >= 0; i--) {
            const subscription = this.subscriptions[i];
            if (subscription.listener !== listener) continue;
            if (subscription.handler) this.emitter.off(subscription.type, subscription.handler);
            this.subscriptions.splice(i, 1);
        }
    }

    /** 注销指定作用域下的全部监听。 */
    public clearScope(scope: string): void {
        for (let i = this.subscriptions.length - 1; i >= 0; i--) {
            const subscription = this.subscriptions[i];
            if (subscription.scope !== scope) continue;
            if (subscription.handler) this.emitter.off(subscription.type, subscription.handler);
            this.subscriptions.splice(i, 1);
        }
    }

    /** 清空全部监听。通常只在战斗销毁或重新初始化时调用。 */
    public clear(): void {
        for (const subscription of this.subscriptions) {
            if (subscription.handler) this.emitter.off(subscription.type, subscription.handler);
        }
        this.subscriptions.length = 0;
        this.afterEmitListeners.length = 0;
    }

    /** 派发事件。mitt 会同步执行监听，调用方应避免在监听中执行长耗时逻辑。 */
    public emit(event: PropRuntimeEvent): void {
        this.emitter.emit(event.type, event);
        for (const listener of [...this.afterEmitListeners]) listener(event);
        for (const subscription of [...this.subscriptions]) {
            if (subscription.phase !== 'after' || subscription.type !== event.type) continue;
            subscription.listener(event);
        }
    }

}
